import { ArrowRightOutlined } from "@payping/icons";
import { webConstants } from "@payping/utils/platformConstants";
import { toFaAlpha } from "@payping/utils/string";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import styles from "../../../assets/scss/Search.module.scss";
import { useAppTranslation } from "../../../utils/useAppTranslation";
import { searchData } from "./SearchData";

interface Props {
  visible?: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

interface RouteData {
  label: string;
  value: string;
}

const Search: React.FC<Props> = (props: Props) => {
  const { visible, setVisible } = props;
  const [searchValue, setSearchValue] = useState<string>("");
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const routes = webConstants.DashboardRoutes;
  const minSearchLength = 3;

  const routeOptions: RouteData[] = [
    { label: t("pageTitles.report.messages"), value: routes.home.messages },
    { label: t("pageTitles.report.itemSale"), value: routes.report.itemSale },
    { label: t("pageTitles.report.overview"), value: routes.home.overview },
    { label: t("pageTitles.report.summation"), value: routes.report.summation },
    { label: t("pageTitles.report.transactions"), value: routes.report.transactions.root },
    { label: t("pageTitles.report.withdraws"), value: routes.report.withdraws.root },
    { label: t("pageTitles.ipg.oauth"), value: routes.ipg.oauth.root },
    { label: t("pageTitles.ipg.oauthCreate"), value: routes.ipg.oauth.create },
    { label: t("pageTitles.ipg.token"), value: routes.ipg.token.root },
    { label: t("pageTitles.ipg.tokenCreate"), value: routes.ipg.token.create },
    { label: t("pageTitles.ipg.services.root"), value: routes.ipg.service.root },
    { label: t("pageTitles.ipg.services.trust"), value: routes.ipg.service.detail.replace(":name", "trust") },
    {
      label: t("pageTitles.ipg.services.githubDonate"),
      value: routes.ipg.service.detail.replace(":name", "githubDonate"),
    },
    {
      label: t("pageTitles.ipg.services.donateScript"),
      value: routes.ipg.service.detail.replace(":name", "donateScript"),
    },
    { label: t("pageTitles.ipg.services.slack"), value: routes.ipg.service.detail.replace(":name", "slack") },
    { label: t("pageTitles.ipg.services.telegram"), value: routes.ipg.service.detail.replace(":name", "telegram") },
    { label: t("pageTitles.ipg.services.kavehNegar"), value: routes.ipg.service.detail.replace(":name", "kavehNegar") },
    { label: t("pageTitles.ipg.services.mailerLite"), value: routes.ipg.service.detail.replace(":name", "mailerLite") },
    { label: t("pageTitles.ipg.services.J2Store"), value: routes.ipg.service.detail.replace(":name", "J2Store") },
    { label: t("pageTitles.ipg.services.prestaShop"), value: routes.ipg.service.detail.replace(":name", "prestaShop") },
    { label: t("pageTitles.ipg.services.whmcs"), value: routes.ipg.service.detail.replace(":name", "whmcs") },
    { label: t("pageTitles.ipg.services.opencart"), value: routes.ipg.service.detail.replace(":name", "opencart") },
    {
      label: t("pageTitles.ipg.services.virtualFreer"),
      value: routes.ipg.service.detail.replace(":name", "virtualFreer"),
    },
    {
      label: t("pageTitles.ipg.services.nopCommerce"),
      value: routes.ipg.service.detail.replace(":name", "nopCommerce"),
    },
    {
      label: t("pageTitles.ipg.services.wooCommerce"),
      value: routes.ipg.service.detail.replace(":name", "wooCommerce"),
    },
    {
      label: t("pageTitles.ipg.services.wooCommerceVIP"),
      value: routes.ipg.service.detail.replace(":name", "wooCommerceVIP"),
    },
    {
      label: t("pageTitles.ipg.services.gravityForms"),
      value: routes.ipg.service.detail.replace(":name", "gravityForms"),
    },
    {
      label: t("pageTitles.ipg.services.easyDigital"),
      value: routes.ipg.service.detail.replace(":name", "easyDigital"),
    },
    {
      label: t("pageTitles.ipg.services.donatePlugin"),
      value: routes.ipg.service.detail.replace(":name", "donatePlugin"),
    },
    { label: t("pageTitles.ipg.services.paidPro"), value: routes.ipg.service.detail.replace(":name", "paidPro") },
    { label: t("pageTitles.ipg.services.proVIP"), value: routes.ipg.service.detail.replace(":name", "proVIP") },
    { label: t("pageTitles.ipg.services.give"), value: routes.ipg.service.detail.replace(":name", "give") },
    {
      label: t("pageTitles.ipg.services.classiPress"),
      value: routes.ipg.service.detail.replace(":name", "classiPress"),
    },
    { label: t("pageTitles.ipg.services.myCred"), value: routes.ipg.service.detail.replace(":name", "myCred") },
    {
      label: t("pageTitles.ipg.services.restrictContentPro"),
      value: routes.ipg.service.detail.replace(":name", "restrictContentPro"),
    },
    { label: t("pageTitles.ipg.services.learnPress"), value: routes.ipg.service.detail.replace(":name", "learnPress") },
    {
      label: t("pageTitles.ipg.services.contactForm7"),
      value: routes.ipg.service.detail.replace(":name", "contactForm7"),
    },
    {
      label: t("pageTitles.ipg.services.WPUserFrontend"),
      value: routes.ipg.service.detail.replace(":name", "WPUserFrontend"),
    },
    { label: t("pageTitles.ipg.document"), value: routes.ipg.document },
    { label: t("pageTitles.requestMoney.list"), value: routes.requestMoney.list },
    { label: t("pageTitles.requestMoney.request"), value: routes.requestMoney.request },
    { label: t("pageTitles.requestMoney.bulkRequest"), value: routes.requestMoney.bulkRequest },
    { label: t("pageTitles.customer.list"), value: routes.customer.list },
    { label: t("pageTitles.customer.create"), value: routes.customer.create },
    { label: t("pageTitles.customer.createBulk"), value: routes.customer.createBulk },
    { label: t("pageTitles.product.list"), value: routes.product.list },
    { label: t("pageTitles.product.archivedList"), value: `${routes.product.list}?isArchived=true` },
    { label: t("pageTitles.product.create"), value: routes.product.create },
    { label: t("pageTitles.product.createBulk"), value: routes.product.createBulk },
    { label: t("pageTitles.product.couponList"), value: routes.product.coupon.list },
    { label: t("pageTitles.product.couponCreate"), value: routes.product.coupon.create },
    { label: t("pageTitles.invoice.list"), value: routes.invoice.list },
    { label: t("pageTitles.invoice.create"), value: routes.invoice.create },
    { label: t("pageTitles.invoice.couponList"), value: routes.invoice.coupon.list },
    { label: t("pageTitles.invoice.couponCreate"), value: routes.invoice.coupon.create },
    // { label: t("pageTitles.platform.list"), value: routes.platform.list },
    { label: t("pageTitles.user.settings.root"), value: routes.user.settings.root },
    { label: t("pageTitles.user.settings.display"), value: routes.user.settings.display },
    { label: t("pageTitles.user.settings.messaging"), value: routes.user.settings.messaging },
    { label: t("pageTitles.user.settings.password"), value: routes.user.settings.password },
    { label: t("pageTitles.user.settings.terminal"), value: routes.user.settings.terminal },
    { label: t("pageTitles.user.settings.2fa"), value: routes.user.settings["2fa"] },
    { label: t("pageTitles.user.requestWithdraw"), value: routes.user.requestWithdraw },
    { label: t("pageTitles.user.upgrade"), value: routes.user.upgrade },
    { label: t("pageTitles.user.profile.root"), value: routes.user.profile.root },
    { label: t("pageTitles.user.profile.personal"), value: routes.user.profile.personal },
    { label: t("pageTitles.user.profile.business"), value: routes.user.profile.business },
    { label: t("pageTitles.user.profile.bank"), value: routes.user.profile.bank },
    { label: t("pageTitles.user.profile.local"), value: routes.user.profile.local },
    { label: t("pageTitles.user.profile.social"), value: routes.user.profile.social },
    { label: t("pageTitles.user.referral"), value: routes.user.referral },
  ];

  useEffect(() => {
    setTimeout(() => {
      if (visible) inputRef.current?.focus();
    }, 100);
    document.getElementsByTagName("body")[0].setAttribute("style", `overflow: ${visible ? "hidden" : ""}`);
  }, [visible]);

  function filterOption(inputValue: string, option?: RouteData): boolean {
    if (inputValue.length < minSearchLength) {
      return false;
    }
    const wordList = inputValue?.split(" ");
    let exists = false;
    wordList?.forEach(
      (word) =>
        option &&
        word &&
        word.length >= minSearchLength &&
        searchData[option.value]?.map((keyword: string) => {
          if (keyword.includes(word)) {
            exists = true;
            return null;
          }
          return null;
        })
    );
    return exists;
  }

  return (
    <div className={classNames(styles.wrapper, visible ? "" : "hidden")}>
      <div className={styles.inputWrapper}>
        <div className={styles.iconWrapper} onClick={() => setVisible(false)}>
          <ArrowRightOutlined width={"14px"} />
        </div>
        <input
          ref={inputRef}
          onChange={(event) => setSearchValue(event.currentTarget.value)}
          className={styles.input}
          placeholder={t("search.placeholder")}
        />
      </div>
      <ul className={styles.searchResultList}>
        {routeOptions
          .filter((option) => filterOption(toFaAlpha(searchValue.toLowerCase()), option))
          .map((route, index) => (
            <li
              key={index}
              onClick={() => {
                navigate(String(route.value));
                setVisible(false);
              }}
            >
              {route.label}
            </li>
          ))}
      </ul>
    </div>
  );
};

export default Search;
